import { useState } from 'react';
import { Stack, Title, Paper, NumberInput, Button, Group, Text, Checkbox } from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import { useCompanies } from '@/hooks/useCompanies';
import { useSendTokens } from '@/hooks/useTokens';
import type { CompanyWithDetails } from '@/types/company';

export function TokenSendPage() {
  const { data: companies, isLoading } = useCompanies();
  const sendTokens = useSendTokens();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [tokenCount, setTokenCount] = useState<number>(10);

  const selectedRecords = (companies ?? []).filter((c) => selectedIds.includes(c.id));

  const handleSend = () => {
    if (selectedIds.length === 0) {
      notifications.show({ title: 'Hinweis', message: 'Bitte wählen Sie mindestens ein Unternehmen.', color: 'yellow' });
      return;
    }

    const companies = selectedRecords.map((c) => ({
      companyId: c.id,
      companyName: c.name,
    }));

    sendTokens.mutate(
      { companies, tokenCount },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Gesendet',
            message: `${tokenCount} Token an ${selectedIds.length} Unternehmen gesendet.`,
            color: 'green',
          });
          setSelectedIds([]);
        },
        onError: () => {
          notifications.show({ title: 'Fehler', message: 'Token konnten nicht gesendet werden.', color: 'red' });
        },
      },
    );
  };

  return (
    <Stack gap="md">
      <Title order={2}>Token-Aktion</Title>

      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <NumberInput
            label="Anzahl Token pro Unternehmen"
            min={1}
            max={10000}
            value={tokenCount}
            onChange={(v) => setTokenCount(Number(v))}
            w={300}
          />

          <Text fw={600}>
            Unternehmen auswählen ({selectedIds.length} ausgewählt)
          </Text>

          <DataTable
            withTableBorder
            borderRadius="sm"
            highlightOnHover
            records={companies ?? []}
            fetching={isLoading}
            selectedRecords={selectedRecords}
            onSelectedRecordsChange={(records: CompanyWithDetails[]) => setSelectedIds(records.map((r) => r.id))}
            columns={[
              { accessor: 'name', title: 'Name', sortable: true },
              { accessor: 'legalForm', title: 'Gesellschaftsform' },
              { accessor: 'email', title: 'E-Mail' },
            ]}
            noRecordsText="Keine Unternehmen vorhanden"
          />

          <Group justify="flex-end">
            <Button
              leftSection={<IconSend size={16} />}
              onClick={handleSend}
              loading={sendTokens.isPending}
              disabled={selectedIds.length === 0}
            >
              {tokenCount} Token an {selectedIds.length} Unternehmen senden
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
